import {
  createAuthorizedHeadRequest,
  createAuthorizedGetRequest,
  fetchUnparsedApi,
  normalizePath,
  nullableString,
  string,
  stringify,
} from '@/services/api';
import { pipe } from 'fp-ts/function';
import { chain, map } from 'fp-ts/TaskEither';
import { getToken } from '@/services/token';
import { fromIOEither } from 'fp-ts/lib/TaskEither';

export type SupportedExportFormat = 'csv' | 'xlsx' | 'xlsx_simple' | 'pdf' | 'zip' | 'docx' | 'batch-export-photos';

export enum SupportedExportFormatEnum {
  CSV = 'csv',
  XLSX = 'xlsx',
  XLSX_SIMPLE = 'xlsx_simple',
  PDF = 'pdf',
  ZIP = 'zip',
  DOCX = 'docx',
  BatchExportPhotos = 'batch-export-photos'
}

export enum SupportedFileVersionsEnum {
  SIMPLE = 'simple',
  EXTENDED = 'extended,'
}

const baseUrl = '/v2/export/inspections';

const createDownloadUrl = (
  url: string,
  format: string,
  timezone: string,
) => (
  token: string,
) => {
  const u = new URL(normalizePath(`${url}`));
  let version: string|null = null;
  if (format === SupportedExportFormatEnum.XLSX_SIMPLE) {
    version = SupportedFileVersionsEnum.SIMPLE;
    format = SupportedExportFormatEnum.XLSX;
  }
  u.search = stringify({
    token: string(token),
    format: string(format),
    timezone: string(timezone),
    version: nullableString(version),
  });
  return u;
};

const createDownloadUrlBatch = (
  url: string,
  format: string,
  timezone: string,
  inspections: string[],
) => (
  token: string,
) => {
  const u = new URL(normalizePath(`${url}`));
  u.search = stringify({
    token: string(token),
    format: string(format),
    timezone: string(timezone),
    inspections: inspections.join(','),
  });
  return u;
};

export const inspectionReportExportUrl = (
  id: number,
  format: SupportedExportFormat,
  timezone: string,
) => pipe(
  getToken(),
  fromIOEither,
  map(createDownloadUrl(`${baseUrl}/${id}`, format, timezone)),
);

export const inspectionPhotosExportUrl = (
  id: number,
  format: SupportedExportFormat,
  timezone: string,
) => pipe(
  getToken(),
  fromIOEither,
  map(createDownloadUrl(`${baseUrl}/photos/${id}`, format, timezone)),
);

export const inspectionsReportExportUrl = (
  inspections: string[],
  format: SupportedExportFormat,
  timezone: string,
) => pipe(
  getToken(),
  fromIOEither,
  map(createDownloadUrlBatch(`${baseUrl}`, format, timezone, inspections)),
);

export const inspectionsReportExportPhotosUrl = (
  ids: string[],
  format: SupportedExportFormat,
  timezone: string,
) => pipe(
  getToken(),
  fromIOEither,
  map(createDownloadUrlBatch(`${baseUrl}/photos`, format, timezone, ids)),
);

export const checkReportExportPhotosExist = (
  url: string,
) => pipe(
  url,
  createAuthorizedHeadRequest,
  chain(fetchUnparsedApi),
);

export const inspectionsReportDownload = (
  url: string,
) => pipe(
  url,
  createAuthorizedGetRequest,
  chain(fetchUnparsedApi),
);
